import { type Dispatch, type SetStateAction, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import { type Swiper as SwiperClass } from 'swiper/types';

import {
    Button,
    ButtonColor,
    ButtonShape,
    ButtonSize,
    ButtonVariant,
    ChevronIcon,
    useIntersection,
} from '@jsmdg/yoshi';
import { useWidgetDataReviews } from '../../hooks/useWidgetDataReviews';
import { HighlightedReview } from '../HighlightedReview';
import { ReviewsStarsRating } from '../ReviewsStarsRating';
import {
    trackDisplayAllClicked,
    trackNextClicked,
    trackOpenReviewSectionClicked,
    trackPrevClicked,
    trackShowReviews,
} from './ReviewsHighlight.tracking';
import styles from './ReviewsHighlight.module.scss';

const messages = defineMessages({
    showAll: {
        defaultMessage: 'Alle anzeigen',
    },
    reviews: {
        defaultMessage: 'Bewertungen',
    },
});

interface ReviewsHighlightProps {
    readonly handleShowAll: () => void;
    readonly openReviewLink: (reviewId: number) => void;
}

const ReviewsHighlight = ({
    handleShowAll,
    openReviewLink,
}: ReviewsHighlightProps): JSX.Element | null => {
    const intl = useIntl();
    const { reviewsWidgetData } = useWidgetDataReviews();
    const galleryRef = useRef<SwiperClass>();
    const [isIntersecting, setTargetElement] = useIntersection({
        triggerOnce: true,
    }) as [boolean, Dispatch<SetStateAction<HTMLElement | null>>];

    useEffect(() => {
        if (isIntersecting && reviewsWidgetData) {
            trackShowReviews();
        }
    }, [isIntersecting, reviewsWidgetData]);

    const showNextReviews = () => {
        trackNextClicked();
        galleryRef.current?.slideNext();
    };

    const showPreviousReviews = () => {
        trackPrevClicked();
        galleryRef.current?.slidePrev();
    };

    if (!reviewsWidgetData) {
        return null;
    }

    return (
        <div
            ref={setTargetElement as (el: HTMLElement | null) => void}
            className={classNames(styles.reviewsBox, 'pt-2x')}
        >
            <div className="d-flex align-items-center justify-content-center">
                <button
                    type="button"
                    className={styles.topButton}
                    onClick={() => {
                        handleShowAll();
                        trackOpenReviewSectionClicked(reviewsWidgetData);
                    }}
                >
                    <div className="d-flex">
                        <div className={styles.averageScore}>
                            {reviewsWidgetData.averageScore.toFixed(1)}
                        </div>
                        <div className="d-flex flex-column">
                            <div className="mt-1x">
                                <ReviewsStarsRating
                                    rating={reviewsWidgetData.averageScore}
                                    iconSize={20}
                                />
                            </div>
                            <div className={styles.totalReviews}>
                                {reviewsWidgetData.totalReview}{' '}
                                {intl.formatMessage(messages.reviews)}
                            </div>
                            <div className={styles.location} />
                        </div>
                    </div>
                </button>
            </div>
            <div className="d-flex flex-column">
                <div>
                    <div className={styles.swiperContainer}>
                        <Swiper
                            className={classNames(styles.gallerySwiper)}
                            onSwiper={(gallerySwiper: SwiperClass) => {
                                galleryRef.current = gallerySwiper;
                            }}
                            pagination={{
                                clickable: true,
                                bulletClass: styles.paginationBullet,
                                bulletActiveClass: styles.paginationBulletActive,
                                horizontalClass: styles.paginationContainer,
                                modifierClass: styles.paginationContainer,
                            }}
                            slidesPerView="auto"
                            spaceBetween={24}
                            watchOverflow
                            slidesOffsetAfter={24}
                            slidesOffsetBefore={24}
                            rewind
                        >
                            {reviewsWidgetData.reviews.map(review => (
                                <SwiperSlide key={`${review.id}-review-slider`}>
                                    <HighlightedReview
                                        openReviewLink={() => openReviewLink(review.id)}
                                        review={review}
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                    <div className="d-inline-flex w-100 mb-3x">
                        <div className={classNames(styles.showAll, 'ml-3x')}>
                            <Button
                                a11yText={intl.formatMessage(messages.showAll)}
                                onClick={() => {
                                    trackDisplayAllClicked();
                                    handleShowAll();
                                }}
                                size={ButtonSize.Small}
                                variant={ButtonVariant.Outlined}
                            >
                                {intl.formatMessage(messages.showAll)}
                            </Button>
                        </div>
                        <div className={classNames('w-50 pr-3x', 'text-right')}>
                            <div className={styles.navigationButtons}>
                                <Button
                                    a11yText={intl.formatMessage(messages.showAll)}
                                    color={ButtonColor.Dark}
                                    onClick={showPreviousReviews}
                                    shape={ButtonShape.Icon}
                                    size={ButtonSize.Small}
                                    variant={ButtonVariant.Outlined}
                                >
                                    <ChevronIcon className={styles.iconPrev} />
                                </Button>
                                <Button
                                    a11yText={intl.formatMessage(messages.showAll)}
                                    color={ButtonColor.Dark}
                                    onClick={showNextReviews}
                                    shape={ButtonShape.Icon}
                                    size={ButtonSize.Small}
                                    variant={ButtonVariant.Outlined}
                                >
                                    <ChevronIcon className={styles.iconNext} />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export { ReviewsHighlight };
